import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BookOpen,
  Users,
  FileText,
  Star,
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
  LayoutDashboard,
  ShieldAlert,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { DashboardStats, RecentActivity } from '../types'
import dashboardService from '../services/dashboardService'
import { shouldShowBookmyyogattcVerificationDocumentsBanner } from '../utils/bookmyyogattcVerificationReminder'

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { selectedSchool, user, loading: authLoading } = useAuth()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([])
  const [loading, setLoading] = useState(true)
  const schoolIsApproved = selectedSchool?.approvalStatus === 'APPROVED'
  /** Matches SchoolLayout verification-documents banner — avoid duplicating a second amber alert. */
  const verificationDocsBannerScenario = shouldShowBookmyyogattcVerificationDocumentsBanner(selectedSchool)
  const showSchoolPendingVerificationCard = !schoolIsApproved && !verificationDocsBannerScenario

  useEffect(() => {
    // Wait for authentication to complete before fetching data
    if (authLoading || !user) {
      setLoading(true)
      return
    }

    // Check if token exists before making API calls
    const token = localStorage.getItem('authToken')
    if (!token) {
      setLoading(false)
      return
    }

    const fetchDashboardData = async () => {
      try {
        const [statsResponse, activitiesResponse] = await Promise.all([
          dashboardService.getDashboardStats(),
          dashboardService.getRecentActivities()
        ])

        if (statsResponse.success) {
          setStats(statsResponse.data)
        }

        if (activitiesResponse.success) {
          setRecentActivities(activitiesResponse.data)
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [user, authLoading])

  const statCards = [
    {
      title: 'Pending Bookings',
      value: stats?.pendingBookings || 0,
      icon: Clock,
      color: 'bg-warning-500',
      change: stats?.pendingBookings ? `${stats.pendingBookings} awaiting action` : 'No pending',
      changeType: stats?.pendingBookings && stats.pendingBookings > 0 ? 'negative' as const : 'positive' as const,
    },
    {
      title: 'Active Courses',
      value: stats?.activeCourses || 0,
      icon: CheckCircle,
      color: 'bg-primary-600',
      change: `${stats?.totalCourses || 0} total courses`,
      changeType: 'positive' as const,
    },
    {
      title: 'Total Reviews',
      value: stats?.totalReviews || 0,
      icon: Star,
      color: 'bg-primary-500',
      change: stats?.averageRating ? `Avg: ${stats.averageRating.toFixed(1)}⭐` : 'No reviews yet',
      changeType: 'positive' as const,
    },
  ]

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return FileText
      case 'course':
        return BookOpen
      case 'review':
        return Star
      default:
        return Users
    }
  }

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'booking':
        return 'text-primary-500'
      case 'course':
        return 'text-primary-600'
      case 'review':
        return 'text-primary-500'
      default:
        return 'text-primary-700'
    }
  }

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
    
    const diffInWeeks = Math.floor(diffInDays / 7)
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-2xl rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                <LayoutDashboard className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg"></div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Welcome back, {selectedSchool?.name}!</h1>
              <p className="text-white/90 text-sm">Here's what's happening with your yoga school today.</p>
            </div>
          </div>
        </div>
      </div>

      {showSchoolPendingVerificationCard && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-amber-50 border border-amber-200 rounded-2xl shadow-sm"
        >
          <div className="p-5 flex items-start gap-4">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
              <ShieldAlert className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-amber-900">School Pending Verification</h2>
              <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                Your school profile is currently under admin review. Once verification is complete, your school will be
                fully approved on the platform. You can continue updating your profile, courses, and documents in the
                meantime.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${stat.color} p-3 rounded-md`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.title}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {stat.value}
                        </div>
                        <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                          stat.changeType === 'positive' ? 'text-primary-600' : 'text-error-600'
                        }`}>
                          <TrendingUp className="self-center flex-shrink-0 h-4 w-4" />
                          <span className="sr-only">
                            {stat.changeType === 'positive' ? 'Increased' : 'Decreased'} by
                          </span>
                          {stat.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activities */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white shadow rounded-lg"
        >
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Activities
            </h3>
            <div className="mt-5">
              <div className="flow-root">
                <ul className="-my-5 divide-y divide-gray-200">
                  {recentActivities.length > 0 ? (
                    recentActivities.map((activity) => {
                      const Icon = getActivityIcon(activity.type)
                      const color = getActivityColor(activity.type)
                      return (
                        <li key={activity.id} className="py-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                              <Icon className={`h-5 w-5 ${color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {activity.title}
                              </p>
                              <p className="text-sm text-gray-500 truncate">
                                {activity.description}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatTimeAgo(activity.timestamp)}
                              </p>
                            </div>
                            {activity.status && (
                              <div className="flex-shrink-0">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  activity.status === 'APPROVED' ? 'bg-primary-100 text-primary-800' :
                                  activity.status === 'PENDING' ? 'bg-warning-100 text-warning-800' :
                                  activity.status === 'REJECTED' ? 'bg-error-100 text-error-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {activity.status}
                                </span>
                              </div>
                            )}
                          </div>
                        </li>
                      )
                    })
                  ) : (
                    <li className="py-8 text-center">
                      <p className="text-sm text-gray-500">No recent activities</p>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white shadow rounded-lg"
        >
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Quick Actions
            </h3>
            <div className="mt-5">
              <div className="grid grid-cols-2 gap-4">
                <button 
                  onClick={() => navigate('/courses/create')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Add Course
                </button>
                <button 
                  onClick={() => navigate('/bookings')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Bookings
                </button>
                <button 
                  onClick={() => navigate('/profile')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Edit Profile
                </button>
                <button 
                  onClick={() => navigate('/profile', { state: { openTab: 'school' } })}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  View School
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Dashboard
